// src/pages/Home/Home.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Wrench, Award } from "lucide-react";

import HeroSection from "../components/HeroSection";
import AnimatedSection from "../components/AnimatedSection";
import ProductCard from "../components/ProductCard";
import ProjectPreview from "../components/ProjectCard";

import heroVideo from "../assets/hero.mp4";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const Home = () => {
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const features = [
    {
      icon: Shield,
      title: "Unmatched Quality",
      desc: "Every product meets strict quality standards with full traceability and reliability.",
    },
    {
      icon: Wrench,
      title: "Expert Fabrication",
      desc: "Advanced equipment and skilled craftsmanship ensure precision and quality in every fabrication.",
    },
    {
      icon: Award,
      title: "Proven Track Record",
      desc: "15+ years delivering steel solutions for major infrastructure projects.",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, projectRes] = await Promise.all([
          axios.get(`${API_BASE}/api/products?starred=true`),
          axios.get(`${API_BASE}/api/projects?starred=true`),
        ]);
        setProducts(productRes.data || []);
        setProjects(projectRes.data || []);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoadingProducts(false);
        setLoadingProjects(false);
      }
    };
    fetchData();
  }, []);

  return (
    <main className="overflow-x-hidden font-inter">
      {/* Hero Section */}
      <HeroSection />
      {/* Features Section */}
      <section className="py-20 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 lg:px-16 grid md:grid-cols-3 gap-10">
          {features.map(({ icon: Icon, title, desc }, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div className="p-8 bg-gradient-to-tr from-white to-green-50 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300 flex flex-col items-start h-full">
                <div className="flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-5">
                  <Icon size={28} className="text-green-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">
                  {title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-20 bg-[#eef5ec] md:bg-[#d2e6d0]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 lg:px-16 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          {/* MOBILE: VIDEO FIRST (Apple style) */}
          <div className="md:hidden w-full rounded-2xl overflow-hidden shadow-md">
            <video
              src={heroVideo}
              className="w-full h-[220px] object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>

          {/* TEXT */}
          <AnimatedSection>
            <div className="bg-white md:bg-transparent rounded-2xl md:rounded-none p-5 md:p-0 shadow-sm md:shadow-none">
              <span className="text-xs font-semibold text-green-600 uppercase tracking-widest">
                About Us
              </span>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4 leading-snug">
                Building the Backbone of Industry
              </h2>

              <p className="text-gray-600 md:text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                For over 15 years, Chelinko has been at the forefront of
                stainless steel fabrication. From custom tables and sinks to
                ventilation systems, canopies, silos, gates, tanks, and hand
                railings, we deliver durable solutions built to last.
              </p>

              <p className="text-gray-600 md:text-gray-700 leading-relaxed mb-6 text-sm sm:text-base">
                Our expertise extends beyond individual fabrications to
                large-scale industry projects, including installations for
                hotels and tea factories, combining precision craftsmanship with
                reliable, high-quality fabrication.
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-hero text-white font-semibold px-6 py-3 rounded-full md:rounded-lg hover:bg-green-700 transition w-full sm:w-auto justify-center"
              >
                Get in Touch <ArrowRight size={16} />
              </Link>
            </div>
          </AnimatedSection>

          {/* DESKTOP VIDEO (UNCHANGED) */}
          <AnimatedSection delay={0.2}>
            <div className="hidden md:block w-full md:aspect-[4/3] md:rounded-2xl overflow-hidden shadow-lg">
              <video
                src={heroVideo}
                className="w-full h-full object-cover md:rounded-2xl"
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
          </AnimatedSection>
        </div>
      </section>
      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8">
          <AnimatedSection>
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <span className="text-sm font-medium text-green-600 uppercase tracking-wider">
                  Our Products
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-1">
                  Featured Products
                </h2>
              </div>

              <Link
                to="/products"
                className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-green-600 hover:gap-2 transition-all"
              >
                View All <ArrowRight size={14} />
              </Link>
            </div>
          </AnimatedSection>

          {/* MOBILE HORIZONTAL SCROLL + DESKTOP GRID */}
          {loadingProducts ? (
            <div className="flex gap-4 overflow-x-auto overflow-y-hidden pb-2 hide-scrollbar scroll-smooth md:grid md:grid-cols-3 sm:grid-cols-2 md:gap-6 md:overflow-visible">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-72 w-64 md:w-full bg-gray-200 rounded-2xl animate-pulse flex-shrink-0"
                />
              ))}
            </div>
          ) : products.length === 0 ? (
            <p className="text-center text-green-200 col-span-full">
              No featured products
            </p>
          ) : (
            <div className="flex gap-4 overflow-x-auto overflow-y-hidden pb-2 hide-scrollbar md:grid md:grid-cols-3 sm:grid-cols-2 md:gap-6 md:overflow-visible">
              {products.map((p, i) => (
                <div key={p._id} className="w-64 md:w-auto flex-shrink-0">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8">
          <AnimatedSection>
            <span className="text-sm font-medium text-green-600 uppercase tracking-wider">
              Case Studies
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-1 mb-12">
              Recent Projects
            </h2>
          </AnimatedSection>

          {/* MOBILE: horizontal scroll | DESKTOP: vertical stack */}
          {loadingProjects ? (
            <div className="flex gap-4 overflow-x-auto overflow-y-hidden pb-2 hide-scrollbar scroll-smooth md:flex md:flex-col md:gap-10 md:overflow-visible">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-72 w-72 md:w-full bg-gray-200 rounded-2xl animate-pulse flex-shrink-0 md:flex-shrink"
                />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <p className="text-center text-green-300">No featured projects</p>
          ) : (
            <div className="flex gap-4 overflow-x-auto overflow-y-hidden pb-2 hide-scrollbar scroll-smooth md:flex md:flex-col md:gap-10 md:overflow-visible">
              {projects.map((p, i) => (
                <div
                  key={p._id}
                  className="w-72 md:w-full flex-shrink-0 md:flex-shrink"
                >
                  <ProjectPreview project={p} fullWidth />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Home;
